import { Component } from '@nicolas-riciotti/light2';

/**
 * <manual-chapternav> — chapter-level controller for the Manual template.
 *
 *  - Updates the bottom pill (current chapter number + title) on scroll.
 *  - Prev/next steps through CHAPTERS on desktop, SECTIONS on mobile.
 *  - Clicking the pill label expands the full chapter list.
 *  - Auto-hides the pill on scroll-down, reveals on scroll-up / touch.
 */
export default class ManualChapterNav extends Component {
  attached() {
    this.chapters = Array.from(document.querySelectorAll('[data-manual-chapter]'));

    this.$label = this.querySelector('[data-manual-current-label]');
    this.$title = this.querySelector('[data-manual-current-title]');
    this.$prev = this.querySelector('[data-manual-prev]');
    this.$next = this.querySelector('[data-manual-next]');

    if (!this.chapters.length) return;

    this.current = -1;
    this.currentSection = -1;
    // Mobile: prev/next step through SECTIONS. Desktop: through CHAPTERS.
    this.isMobile = window.innerWidth <= 600;
    if (this.isMobile) {
      this._onPrev = () => this.navigateToSection(this.currentSection - 1);
      this._onNext = () => this.navigateToSection(this.currentSection + 1);
    } else {
      this._onPrev = () => this.navigateTo(this.current - 1);
      this._onNext = () => this.navigateTo(this.current + 1);
    }
    this.$prev?.addEventListener('click', this._onPrev);
    this.$next?.addEventListener('click', this._onNext);

    this.onIntersect = this.onIntersect.bind(this);
    this.observer = new IntersectionObserver(this.onIntersect, {
      rootMargin: '-40% 0px -55% 0px',
      threshold: 0,
    });
    this.chapters.forEach((chapter) => this.observer.observe(chapter));

    // Mobile: track the current SECTION (sub-topic) so the bottom nav can step
    // through sections — chapter line stays, section name below changes.
    if (this.isMobile) {
      this.sections = Array.from(document.querySelectorAll('[data-manual-topic]'));
      this.sectionObserver = new IntersectionObserver((entries) => {
        if (this._navLock) return;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const i = this.sections.indexOf(entry.target);
          if (i !== -1) this.setCurrentSection(i);
        });
      }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
      this.sections.forEach((s) => this.sectionObserver.observe(s));
    }

    // Bottom-pill chapter selector: click the label to expand the full list.
    this.$toggle = this.querySelector('[data-manual-toggle]');
    this.rows = Array.from(this.querySelectorAll('[data-manual-goto]'));
    this._onToggle = (e) => {
      e.stopPropagation();
      this.classList.toggle('is-open');
      if (this.classList.contains('is-open')) this._openedAt = performance.now();
    };
    this.$toggle?.addEventListener('click', this._onToggle);
    this._rowHandlers = [];
    this.rows.forEach((row) => {
      const idx = Number(row.getAttribute('data-manual-goto'));
      const handler = (e) => { e.preventDefault(); this.classList.remove('is-open'); this.navigateTo(idx); };
      row.addEventListener('click', handler);
      this._rowHandlers.push({ row, handler });
    });
    this._onDocClick = (e) => {
      if (this.classList.contains('is-open') && !this.contains(e.target)) this.classList.remove('is-open');
    };
    this._onKey = (e) => { if (e.key === 'Escape') this.classList.remove('is-open'); };
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);

    // Auto-hide the pill on scroll down; reveal on scroll up or touch.
    this._lastY = window.scrollY || 0;
    this._onScroll = () => {
      if (this.classList.contains('is-open')) {
        // Don't dismiss the selector the instant the user scrolls — give it a moment.
        if (performance.now() - (this._openedAt || 0) > 900) this.classList.remove('is-open');
        return;
      }
      const y = window.scrollY || 0;
      const delta = y - this._lastY;
      if (Math.abs(delta) < 4) return;
      if (delta > 0 && y > 120 && !this._cursorNearBottom) this.classList.add('is-hidden');
      else if (delta < 0 || this._cursorNearBottom) this.classList.remove('is-hidden');
      this._lastY = y;
    };
    this._onTouch = () => this.classList.remove('is-hidden');
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('touchstart', this._onTouch, { passive: true });

    // The hidden pill is pointer-events-none and translated off screen, so it
    // can't be hovered directly — moving the cursor into the bottom strip of
    // the viewport brings it back (and keeps it from re-hiding while there).
    this._cursorNearBottom = false;
    if (!this.isMobile) {
      this._onMouseMove = (e) => {
        const near = e.clientY > window.innerHeight - 120;
        if (near === this._cursorNearBottom) return;
        this._cursorNearBottom = near;
        if (near) this.classList.remove('is-hidden');
      };
      window.addEventListener('mousemove', this._onMouseMove, { passive: true });
    }

    // Seed the pill with chapter 1; the scroll-spy takes over from here (and
    // corrects immediately when the page loads deep-linked to a later chapter).
    this.setCurrent(0);
    if (this.isMobile) this.setCurrentSection(0);
  }

  onIntersect(entries) {
    if (this._navLock) return; // ignore mid-scroll intersections during a click/prev-next jump
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const index = this.chapters.indexOf(entry.target);
      if (index !== -1) this.setCurrent(index);
    });
  }

  setCurrent(index) {
    if (index === this.current) return;
    this.current = index;

    const chapter = this.chapters[index];
    if (!chapter) return;

    // On mobile the pill text is driven by the SECTION nav instead.
    if (!this.isMobile) {
      if (this.$label) this.$label.textContent = chapter.getAttribute('data-chapter-label') || '';
      if (this.$title) this.$title.textContent = chapter.getAttribute('data-chapter-title') || '';
    }

    this.rows?.forEach((row) => {
      row.classList.toggle('is-active', Number(row.getAttribute('data-manual-goto')) === index);
    });

    if (!this.isMobile) {
      const atStart = index <= 0;
      const atEnd = index >= this.chapters.length - 1;
      this.$prev?.classList.toggle('opacity-30', atStart);
      this.$next?.classList.toggle('opacity-30', atEnd);
      if (this.$prev) this.$prev.disabled = atStart;
      if (this.$next) this.$next.disabled = atEnd;
    }
  }

  // Mobile section nav: keep the chapter line, swap the section name below.
  setCurrentSection(index) {
    if (index === this.currentSection) return;
    this.currentSection = index;
    const section = this.sections[index];
    if (!section) return;
    if (this.$label) this.$label.textContent = section.getAttribute('data-chapter-label') || '';

    // Front matter and appendices can have an untitled section. Keep its
    // chapter title rather than clearing the pill when the preview timer ends.
    const chapterTitle = section.getAttribute('data-chapter-title') || '';
    const sectionLabel = section.getAttribute('data-section-label')?.trim() || chapterTitle;
    clearTimeout(this._sectionPeekTimer);
    if (this.$title) {
      // On a chapter's first section, sneak in the chapter title, then settle on the section name.
      if (section.getAttribute('data-section-first') === '1') {
        this.$title.textContent = chapterTitle || sectionLabel;
        this._sectionPeekTimer = setTimeout(() => {
          if (this.$title) this.$title.textContent = sectionLabel;
        }, 900);
      } else {
        this.$title.textContent = sectionLabel;
      }
    }

    const atStart = index <= 0;
    const atEnd = index >= this.sections.length - 1;
    this.$prev?.classList.toggle('opacity-30', atStart);
    this.$next?.classList.toggle('opacity-30', atEnd);
    if (this.$prev) this.$prev.disabled = atStart;
    if (this.$next) this.$next.disabled = atEnd;
  }

  navigateToSection(index) {
    if (index < 0 || index >= this.sections.length || index === this.currentSection) return;
    this._navLock = true;
    clearTimeout(this._navLockTimer);
    this.setCurrentSection(index);
    this.sections[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
    this._navLockTimer = setTimeout(() => { this._navLock = false; this._lastY = window.scrollY || 0; }, 1000);
  }

  navigateTo(index) {
    if (index < 0 || index >= this.chapters.length || index === this.current) return;
    // Set the target chapter immediately and lock the scroll-spy so passing
    // chapters don't swap the pill text during the scroll.
    this._navLock = true;
    clearTimeout(this._navLockTimer);
    this.setCurrent(index);
    this.chapters[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
    this._navLockTimer = setTimeout(() => {
      this._navLock = false;
      this._lastY = window.scrollY || 0;
    }, 1000);
  }

  detached() {
    this.observer?.disconnect();
    this.sectionObserver?.disconnect();
    this.$prev?.removeEventListener('click', this._onPrev);
    this.$next?.removeEventListener('click', this._onNext);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('touchstart', this._onTouch);
    if (this._onMouseMove) window.removeEventListener('mousemove', this._onMouseMove);
    clearTimeout(this._navLockTimer);
    clearTimeout(this._sectionPeekTimer);
    this.$toggle?.removeEventListener('click', this._onToggle);
    this._rowHandlers?.forEach(({ row, handler }) => row.removeEventListener('click', handler));
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }
}

customElements.define('manual-chapternav', ManualChapterNav);
